<template>
  <div class="admin-view">
    <div class="admin-toolbar">
      <div>
        <h3>用户管理</h3>
        <p class="admin-sub">添加用户、分配角色与「登录新账号」权限，并把微信机器人账号分配给用户。默认新用户无任何权限。</p>
      </div>
      <el-button type="primary" @click="openAddUser">添加用户</el-button>
    </div>

    <el-table :data="users" v-loading="loading" border stripe size="small">
      <el-table-column prop="username" label="用户 (邮箱)" min-width="200" />
      <el-table-column label="角色" width="140">
        <template #default="{ row }">
          <el-select v-model="row.role" size="small" style="width: 110px" @change="save(row)">
            <el-option label="普通用户" value="user" />
            <el-option label="管理员" value="admin" />
          </el-select>
        </template>
      </el-table-column>
      <el-table-column label="登录新账号" width="110" align="center">
        <template #default="{ row }">
          <el-switch v-model="row.canCreateAccount" @change="save(row)" />
        </template>
      </el-table-column>
      <el-table-column label="负责的微信账号" min-width="200">
        <template #default="{ row }">
          <span class="count">{{ countFor(row.username) }} 个</span>
          <el-button link type="primary" size="small" @click="openAssign(row)">分配账号</el-button>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="90" align="center">
        <template #default="{ row }">
          <el-button link type="danger" size="small" @click="remove(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- add user -->
    <el-dialog v-model="addVisible" title="添加用户" width="420px">
      <el-form label-width="92px">
        <el-form-item label="用户(邮箱)">
          <el-input v-model="newUser.username" placeholder="name@d2ssoft.com" />
        </el-form-item>
        <el-form-item label="角色">
          <el-select v-model="newUser.role" style="width: 100%">
            <el-option label="普通用户" value="user" />
            <el-option label="管理员" value="admin" />
          </el-select>
        </el-form-item>
        <el-form-item label="登录新账号">
          <el-switch v-model="newUser.canCreateAccount" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addVisible = false">取消</el-button>
        <el-button type="primary" @click="addUser">保存</el-button>
      </template>
    </el-dialog>

    <!-- assign accounts -->
    <el-dialog v-model="assignVisible" :title="`分配账号 — ${assignUser}`" width="480px">
      <p class="assign-hint">选择该用户负责的微信机器人账号（可搜索昵称/微信号）。</p>
      <el-select
        v-model="assignSelected"
        multiple
        filterable
        collapse-tags
        collapse-tags-tooltip
        placeholder="选择账号"
        style="width: 100%"
      >
        <el-option
          v-for="a in accounts"
          :key="a.wcId"
          :label="accountLabel(a)"
          :value="a.wcId"
        />
      </el-select>
      <template #footer>
        <el-button @click="assignVisible = false">取消</el-button>
        <el-button type="primary" @click="saveAssign">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import type { WechatAccount } from "../api/models";
import {
  getUsers,
  saveUser,
  removeUser,
  getUserAccounts,
  saveUserAccounts,
  getWechatAccounts,
  type ManagedUser,
} from "../api/http";

const users = ref<ManagedUser[]>([]);
const accounts = ref<WechatAccount[]>([]);
const counts = ref<Record<string, number>>({});
const loading = ref(false);

const addVisible = ref(false);
const newUser = ref<{ username: string; role: "admin" | "user"; canCreateAccount: boolean }>({
  username: "",
  role: "user",
  canCreateAccount: false,
});

const assignVisible = ref(false);
const assignUser = ref("");
const assignSelected = ref<string[]>([]);

function accountLabel(a: WechatAccount): string {
  return `${a.nickName || a.wAccount || "(未命名)"} · ${a.wcId}`;
}
function countFor(username: string): number {
  return counts.value[username] ?? 0;
}

async function loadAll() {
  loading.value = true;
  try {
    const [uRes, aRes] = await Promise.all([getUsers(), getWechatAccounts()]);
    users.value = uRes.successful && uRes.result ? uRes.result : [];
    accounts.value = aRes.successful && aRes.result ? aRes.result : [];
    // per-user assigned counts (users are few)
    const entries = await Promise.all(
      users.value.map(async (u) => {
        const r = await getUserAccounts(u.username).catch(() => null);
        return [u.username, r && r.successful && r.result ? r.result.length : 0] as const;
      }),
    );
    counts.value = Object.fromEntries(entries);
  } catch {
    ElMessage.error("加载失败");
  } finally {
    loading.value = false;
  }
}

async function save(row: ManagedUser) {
  const res = await saveUser(row.username, { role: row.role, canCreateAccount: !!row.canCreateAccount });
  if (res.successful) ElMessage.success("已保存");
  else ElMessage.error(res.message || "保存失败");
}

function openAddUser() {
  newUser.value = { username: "", role: "user", canCreateAccount: false };
  addVisible.value = true;
}
async function addUser() {
  const name = newUser.value.username.trim();
  if (!name) { ElMessage.warning("请输入用户名"); return; }
  const res = await saveUser(name, { role: newUser.value.role, canCreateAccount: newUser.value.canCreateAccount });
  if (res.successful) {
    ElMessage.success("已添加");
    addVisible.value = false;
    await loadAll();
  } else {
    ElMessage.error(res.message || "添加失败");
  }
}

async function remove(row: ManagedUser) {
  try {
    await ElMessageBox.confirm(`删除用户 ${row.username}？该用户将无法登录。`, "确认", { type: "warning" });
  } catch {
    return;
  }
  const res = await removeUser(row.username);
  if (res.successful) { ElMessage.success("已删除"); await loadAll(); }
  else ElMessage.error(res.message || "删除失败（超管不可删）");
}

async function openAssign(row: ManagedUser) {
  assignUser.value = row.username;
  assignSelected.value = [];
  assignVisible.value = true;
  const res = await getUserAccounts(row.username).catch(() => null);
  if (res && res.successful && res.result) assignSelected.value = res.result;
}
async function saveAssign() {
  const res = await saveUserAccounts(assignUser.value, assignSelected.value);
  if (res.successful) {
    ElMessage.success("已保存");
    counts.value[assignUser.value] = assignSelected.value.length;
    assignVisible.value = false;
  } else {
    ElMessage.error(res.message || "保存失败");
  }
}

onMounted(loadAll);
</script>

<style scoped>
.admin-view { padding: 4px; }
.admin-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.admin-toolbar h3 { margin: 0; }
.admin-sub { margin: 4px 0 0; font-size: 12px; color: #909399; max-width: 620px; }
.count { margin-right: 10px; color: #606266; }
.assign-hint { font-size: 12px; color: #909399; margin: 0 0 10px; }
</style>
